import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { getSelectedCustomer } from '../../../../../../_augmentt/selectors/customers'
import ViewEmployeeDataModal from '../../../modals/ViewEmployeeDataModal'
import { EMPTY_VALUE, TONE } from '../../data/constants'
import { formatRelativeDate } from '../../data/reportCopy'
import Chip from '../../components/Chip/Chip'
import EmployeeUserCell from '../../components/EmployeeUserCell/EmployeeUserCell'
import EmptyState from '../../components/EmptyState/EmptyState'
import ReportTable from '../../components/ReportTable/ReportTable'
import SubBlock from '../../components/SubBlock/SubBlock'
import styles from './IdentityAccessSection.module.scss'

const MFA_ISSUE_MESSAGE_IDS = {
  not_registered: 'AI_RISK_REPORT.IDENTITY.MFA.ISSUE_NOT_REGISTERED',
  not_phishing_resistant: 'AI_RISK_REPORT.IDENTITY.MFA.ISSUE_NOT_PHISHING_RESISTANT',
}

const MFA_ISSUE_TONES = {
  not_registered: TONE.DANGER,
  not_phishing_resistant: TONE.WARN,
}

const getTableFooter = ({ shown, total }) => {
  if (shown === 0 || total <= shown) return null

  return <FormattedMessage id="AI_RISK_REPORT.IDENTITY.MFA.TABLE_FOOTER" values={{ shown, total }} />
}

export default function Mfa({ summary, mfaUsers, score, gap = null }) {
  const intl = useIntl()
  const selectedCustomer = useSelector(getSelectedCustomer)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const openEmployee = useCallback((employee) => setSelectedEmployee(employee), [])
  const closeEmployee = useCallback(() => setSelectedEmployee(null), [])
  const mfa = summary.users?.mfa ?? {}
  const isUnavailable = Boolean(gap)
  const assessed = mfa.assessed ?? 0
  const registered = mfa.registered ?? 0
  const notRegistered = Math.max(0, assessed - registered)
  const notPhishingResistant = Number.isFinite(mfa.nonPhishingResistantCount) ? mfa.nonPhishingResistantCount : null
  let notRegisteredTone = TONE.NEUTRAL
  if (!isUnavailable) notRegisteredTone = notRegistered > 0 ? TONE.DANGER : TONE.OK
  let notPhishingResistantTone = TONE.NEUTRAL
  if (notPhishingResistant !== null) {
    notPhishingResistantTone = notPhishingResistant > 0 ? TONE.DANGER : TONE.OK
  }

  const stats = [
    {
      key: 'registered',
      value: isUnavailable ? EMPTY_VALUE : registered,
      denom: isUnavailable ? undefined : assessed,
      tone: isUnavailable ? TONE.NEUTRAL : TONE.OK,
      label: <FormattedMessage id="AI_RISK_REPORT.IDENTITY.MFA.STAT_REGISTERED" />,
    },
    {
      key: 'notRegistered',
      value: isUnavailable ? EMPTY_VALUE : notRegistered,
      denom: isUnavailable ? undefined : assessed,
      tone: notRegisteredTone,
      label: <FormattedMessage id="AI_RISK_REPORT.IDENTITY.MFA.STAT_NOT_REGISTERED" />,
    },
    {
      key: 'notPhishingResistant',
      value: isUnavailable ? EMPTY_VALUE : (notPhishingResistant ?? EMPTY_VALUE),
      denom: isUnavailable || notPhishingResistant === null ? undefined : registered,
      tone: isUnavailable ? TONE.NEUTRAL : notPhishingResistantTone,
      label: <FormattedMessage id="AI_RISK_REPORT.IDENTITY.MFA.STAT_NOT_PHISHING_RESISTANT" />,
    },
  ]
  const columns = [
    {
      key: 'user',
      header: <FormattedMessage id="AI_RISK_REPORT.COL.USER" />,
      render: (row) => (
        <EmployeeUserCell row={row} onOpen={openEmployee} secondary={row.mail || row.userPrincipalName} />
      ),
      sortable: true,
      sortValue: (row) => (row.displayName || row.userPrincipalName || '').toLowerCase(),
      searchValue: (row) => [row.displayName, row.mail, row.userPrincipalName],
    },
    {
      key: 'department',
      header: <FormattedMessage id="AI_RISK_REPORT.COL.DEPARTMENT" />,
      render: (row) => row.department || EMPTY_VALUE,
      sortable: true,
      sortValue: (row) => row.department || '',
      searchValue: (row) => row.department || EMPTY_VALUE,
    },
    {
      key: 'lastSignIn',
      header: <FormattedMessage id="AI_RISK_REPORT.SKELETON.COL_LAST_SIGNIN" />,
      render: (row) => formatRelativeDate(intl, row.lastSignInAt),
      sortable: true,
      sortValue: (row) => row.lastSignInAt || '',
      searchValue: (row) => formatRelativeDate(intl, row.lastSignInAt),
    },
    {
      key: 'issue',
      header: <FormattedMessage id="AI_RISK_REPORT.COL_ISSUE" />,
      render: (row) => (
        <Chip tone={MFA_ISSUE_TONES[row.issue] ?? TONE.NEUTRAL}>
          <FormattedMessage id={MFA_ISSUE_MESSAGE_IDS[row.issue] ?? 'AI_RISK_REPORT.LABEL.UNKNOWN'} />
        </Chip>
      ),
      sortable: true,
      sortValue: (row) =>
        intl.formatMessage({ id: MFA_ISSUE_MESSAGE_IDS[row.issue] ?? 'AI_RISK_REPORT.LABEL.UNKNOWN' }),
      searchValue: (row) =>
        intl.formatMessage({ id: MFA_ISSUE_MESSAGE_IDS[row.issue] ?? 'AI_RISK_REPORT.LABEL.UNKNOWN' }),
    },
  ]
  const rows = (mfaUsers.items ?? []).map((user) => ({ ...user, key: user.entityId }))
  const { total } = mfaUsers
  return (
    <SubBlock
      titleId="AI_RISK_REPORT.IDENTITY.MFA.TITLE"
      subtitleId="AI_RISK_REPORT.IDENTITY.MFA.SUBTITLE"
      score={score.value}
      tone={score.tone}
      gap={gap}
      stats={stats}
    >
      <ReportTable
        columns={columns}
        rows={rows}
        totalRows={total}
        affectedEntityLabelId="AI_RISK_REPORT.TABLE.ENTITY.USERS"
        exportTableClassName={styles.identityUserExportTable}
        footer={getTableFooter({ shown: rows.length, total })}
        empty={
          <EmptyState
            messageId={gap ? 'AI_RISK_REPORT.DATA_NOT_ASSESSED' : 'AI_RISK_REPORT.IDENTITY.MFA.NO_GAPS'}
            values={gap ? { dataType: 'MFA registration' } : undefined}
            tone={gap ? TONE.NEUTRAL : TONE.OK}
          />
        }
      />
      {selectedEmployee && (
        <ViewEmployeeDataModal
          show
          onHide={closeEmployee}
          tableRowEmployee={selectedEmployee}
          selectedCustomer={selectedCustomer}
        />
      )}
    </SubBlock>
  )
}

const summaryPropType = PropTypes.shape({
  users: PropTypes.shape({
    mfa: PropTypes.shape({
      assessed: PropTypes.number,
      registered: PropTypes.number,
      registeredPct: PropTypes.number,
      nonPhishingResistantCount: PropTypes.number,
    }),
  }),
})

Mfa.propTypes = {
  summary: summaryPropType.isRequired,
  mfaUsers: PropTypes.shape({
    total: PropTypes.number,
    items: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  score: PropTypes.shape({ value: PropTypes.number, tone: PropTypes.string }).isRequired,
  gap: PropTypes.shape({ reason: PropTypes.string }),
}
